/**
 * Query definitions for all data sources used in the application.
 * These are displayed in the QueryInfoButton component to help users
 * understand what queries are being executed to fetch data.
 */

export const TENANT_ADOPTION_QUERIES = {
  report: {
    name: 'Tenant Adoption Report',
    description: 'Retrieves daily tenant adoption metrics including new onboarded consumers, logins, and cumulative adoption stats for a date range.',
    endpoint: '/reports/tenant-adoption',
    query: `WITH
tenant_details AS (
    SELECT DISTINCT
        tenant_code,
        tenant_name,
        sponsor_code,
        sponsor_name,
        customer_name
    FROM etl_core.ref_tenant
    WHERE has_client_reporting = TRUE
),
tenant_active_days AS (
    SELECT
        ad.tenant_code,
        ad.date_day
    FROM etl_core.ref_tenant_active_days ad
),
consumer_base AS (
    SELECT
        consumer_id,
        consumer_code,
        tenant_code,
        deleted_consumer_key
    FROM etl_core.ref_consumer_base
    WHERE deleted_consumer_key = FALSE
),
tenant_benefits AS (
    SELECT DISTINCT
        tenant_code,
        has_benefits
    FROM etl_core.ref_tenant
),
verified_consumers AS (
    SELECT
        consumer_code,
        tenant_code,
        MIN(
            (CASE
                WHEN create_ts::date > '2025-07-17'
                THEN create_ts::date
                ELSE NULL
            END)
        ) AS first_verified_create_dt
    FROM huser.consumer_history
    WHERE onboarding_state IN ('AGREEMENT_VERIFIED', 'VERIFIED')
        AND delete_nbr = 0
    GROUP BY consumer_code, tenant_code
),
first_completed_task AS (
    SELECT
        ct.consumer_code,
        ct.tenant_code,
        MIN(DATE(ct.task_completed_ts)) AS first_task_completed_ts
    FROM task.consumer_task ct
    INNER JOIN consumer_base cb
        ON ct.consumer_code = cb.consumer_code
        AND ct.tenant_code = cb.tenant_code
    WHERE ct.task_status = 'COMPLETED'
        AND ct.task_completed_ts IS NOT NULL
        AND ct.delete_nbr IN (0, -10)
    GROUP BY ct.consumer_code, ct.tenant_code
),
first_login_dates AS (
    SELECT
        cb.consumer_id,
        cb.tenant_code,
        cb.consumer_code,
        c.onboarding_state,
        MIN(cl.login_ts::date) AS first_login_dt
    FROM huser.consumer_login cl
    INNER JOIN consumer_base cb
        ON cl.consumer_id = cb.consumer_id
    INNER JOIN huser.consumer c
        ON cl.consumer_id = c.consumer_id
    GROUP BY cb.consumer_id, cb.tenant_code, cb.consumer_code, c.onboarding_state
),
tenant_program_dates AS (
    -- program_start_date is the earliest of: active-days spine,
    -- first verified date, first login date, first completed task date
    WITH active_days AS (
        SELECT tenant_code, MIN(date_day) AS min_active_day
        FROM tenant_active_days
        GROUP BY tenant_code
    ),
    verified AS (
        SELECT tenant_code, MIN(first_verified_create_dt) AS min_verified_dt
        FROM verified_consumers
        GROUP BY tenant_code
    ),
    logins AS (
        SELECT tenant_code, MIN(first_login_dt) AS min_login_dt
        FROM first_login_dates
        GROUP BY tenant_code
    ),
    tasks AS (
        SELECT tenant_code, MIN(first_task_completed_ts) AS min_task_dt
        FROM first_completed_task
        GROUP BY tenant_code
    )
    SELECT
        ad.tenant_code,
        LEAST(
            ad.min_active_day,
            COALESCE(v.min_verified_dt, ad.min_active_day),
            COALESCE(l.min_login_dt, ad.min_active_day),
            COALESCE(t.min_task_dt, ad.min_active_day)
        ) AS program_start_date
    FROM active_days ad
    LEFT JOIN verified v ON ad.tenant_code = v.tenant_code
    LEFT JOIN logins l ON ad.tenant_code = l.tenant_code
    LEFT JOIN tasks t ON ad.tenant_code = t.tenant_code
),
platform_adoption AS (
    -- Driven from consumer_base (not first_login_dates alone) so a
    -- verified/task-completed consumer with no login still gets a row
    SELECT
        cb.consumer_id,
        cb.tenant_code,
        cb.consumer_code,
        (CASE
            WHEN (
                COALESCE(fld.first_login_dt, vc.first_verified_create_dt) IS NOT NULL
                AND (
                    (COALESCE(tb.has_benefits, FALSE) = FALSE)
                    OR (COALESCE(tb.has_benefits, FALSE) = TRUE
                        AND (c.onboarding_state IN ('AGREEMENT_VERIFIED', 'VERIFIED')
                             OR vc.first_verified_create_dt IS NOT NULL))
                )
            )
            OR (fct.first_task_completed_ts IS NOT NULL)
            THEN TRUE
            ELSE FALSE
        END) AS has_adopted,
        -- adoption_dt calculation...
    FROM consumer_base cb
    LEFT JOIN huser.consumer c ON cb.consumer_id = c.consumer_id
    LEFT JOIN first_login_dates fld ON cb.consumer_id = fld.consumer_id
    LEFT JOIN tenant_benefits tb ON cb.tenant_code = tb.tenant_code
    LEFT JOIN verified_consumers vc ON cb.consumer_code = vc.consumer_code AND cb.tenant_code = vc.tenant_code
    LEFT JOIN first_completed_task fct ON cb.consumer_code = fct.consumer_code AND cb.tenant_code = fct.tenant_code
),
tenant_consumer_login_dates AS (
    SELECT
        cb.consumer_id,
        cl.login_ts::date AS login_dt,
        cb.tenant_code,
        COUNT(cl.consumer_login_id) AS count_logins
    FROM huser.consumer_login cl
    INNER JOIN consumer_base cb ON cl.consumer_id = cb.consumer_id
    GROUP BY cb.consumer_id, cl.login_ts::date, cb.tenant_code
),
-- Additional CTEs...
daily_adoption_metrics AS (
    SELECT
        tad.tenant_code,
        tad.date_day,
        COUNT(DISTINCT CASE
            WHEN pa.has_adopted = TRUE
                AND pa.adoption_dt >= tpd.program_start_date
                AND pa.adoption_dt <= tad.date_day
            THEN pa.consumer_id
        END) AS total_adopted_consumers,
        COUNT(DISTINCT CASE
            WHEN pa.has_adopted = TRUE AND pa.adoption_dt = tad.date_day
            THEN pa.consumer_id
        END) AS new_adopted_consumers
    FROM tenant_active_days tad
    INNER JOIN tenant_program_dates tpd ON tad.tenant_code = tpd.tenant_code
    LEFT JOIN platform_adoption pa ON tad.tenant_code = pa.tenant_code
    GROUP BY tad.tenant_code, tad.date_day
)
SELECT
    tad.date_day,
    td.tenant_name,
    COALESCE(dam.new_adopted_consumers, 0) AS new_adopted_consumers,
    COALESCE(tld.count_logins, 0) AS count_logins,
    COALESCE(dam.total_adopted_consumers, 0) AS total_adopted_consumers,
    COALESCE(ttm.total_members, 0) AS total_members,
    CASE
        WHEN COALESCE(ttm.total_members, 0) > 0
        THEN ROUND((COALESCE(dam.total_adopted_consumers, 0)::numeric / ttm.total_members) * 100, 2)
        ELSE 0
    END AS percentage_onboarded
FROM tenant_active_days tad
INNER JOIN tenant_details td ON tad.tenant_code = td.tenant_code
-- Additional JOINs...
WHERE tad.date_day BETWEEN $1 AND $2
ORDER BY ttm.total_members DESC NULLS LAST, td.tenant_name, tad.date_day DESC`,
  },
  summary: {
    name: 'Tenant Adoption Summary',
    description: 'Retrieves overall summary metrics for tenant adoption including total onboarded, total logged in, and active tenant count.',
    endpoint: '/reports/tenant-adoption/summary',
    query: `WITH
tenant_details AS (
    SELECT DISTINCT tenant_code, tenant_name
    FROM etl_core.ref_tenant
    WHERE has_client_reporting = TRUE
),
-- Similar CTEs as report query...
total_logins AS (
    SELECT
        cb.tenant_code,
        COUNT(DISTINCT cb.consumer_id) AS total_unique_logged_in
    FROM huser.consumer_login cl
    INNER JOIN consumer_base cb ON cl.consumer_id = cb.consumer_id
    INNER JOIN tenant_program_dates tpd ON cb.tenant_code = tpd.tenant_code
    WHERE cl.login_ts::date >= tpd.program_start_date
    GROUP BY cb.tenant_code
)
SELECT
    SUM(CASE
        WHEN pa.has_adopted = TRUE AND pa.adoption_dt >= tpd.program_start_date
        THEN 1 ELSE 0
    END) AS total_members_onboarded,
    (SELECT SUM(total_unique_logged_in) FROM total_logins) AS total_members_logged_in,
    COUNT(DISTINCT td.tenant_code) AS total_tenants
FROM tenant_details td
LEFT JOIN tenant_program_dates tpd ON td.tenant_code = tpd.tenant_code
LEFT JOIN platform_adoption pa ON td.tenant_code = pa.tenant_code`,
  },
  today: {
    name: 'Tenant Adoption Today',
    description: 'Retrieves today\'s tenant adoption metrics for all tenants.',
    endpoint: '/reports/tenant-adoption/today',
    query: `-- Same as report query with WHERE tad.date_day = CURRENT_DATE`,
  },
};

export const CARD_METRICS_QUERIES = {
  report: {
    name: 'Card Metrics Report',
    description: 'Retrieves daily card metrics including orders, shipments, and activations per tenant for a date range.',
    endpoint: '/reports/card-metrics',
    query: `WITH
card_tenant_mapping AS (
    SELECT DISTINCT
        proxy_number AS card_number_proxy,
        tenant_code,
        consumer_code
    FROM fis.consumer_account
    WHERE delete_nbr = 0 AND proxy_number IS NOT NULL

    UNION

    SELECT DISTINCT
        proxy_number AS card_number_proxy,
        tenant_code,
        consumer_code
    FROM fis.consumer_account_history
    WHERE delete_nbr = 0 AND proxy_number IS NOT NULL
),
card_orders AS (
    SELECT DISTINCT
        CAST(card_creation_date AS DATE) AS date_day,
        card_number_proxy
    FROM fis.non_monetary_transaction
    WHERE card_creation_date IS NOT NULL
        AND source_code_desc NOT IN ('Account Access', 'Create a new purse')
),
shipped_cards AS (
    SELECT DISTINCT
        CAST(ship_date AS DATE) AS date_day,
        proxy_number AS card_number_proxy
    FROM ab_corp.workorder_shipping_events
    WHERE ship_date IS NOT NULL
),
activated_cards AS (
    SELECT DISTINCT
        CAST("timestamp_" AS DATE) AS date_day,
        card_number_proxy
    FROM fis.non_monetary_transaction
    WHERE card_status_desc = 'Active' AND "timestamp_" IS NOT NULL
),
-- Aggregate by tenant and date...
SELECT
    atd.date_day,
    tr.tenant_name,
    COALESCE(tdco.cards_ordered_count, 0) AS cards_ordered_count,
    COALESCE(tds.cards_shipped_count, 0) AS cards_shipped_count,
    COALESCE(tda.cards_activated_count, 0) AS cards_activated_count
FROM all_tenant_dates atd
LEFT JOIN tenant_ref tr ON atd.tenant_code = tr.tenant_code
-- Additional JOINs...
WHERE atd.date_day BETWEEN $1 AND $2
ORDER BY tr.tenant_name, atd.date_day DESC`,
  },
  summary: {
    name: 'Card Metrics Summary',
    description: 'Retrieves total card counts across all tenants.',
    endpoint: '/reports/card-metrics/summary',
    query: `WITH
card_tenant_mapping AS (
    SELECT DISTINCT proxy_number AS card_number_proxy, tenant_code
    FROM fis.consumer_account
    WHERE delete_nbr = 0 AND proxy_number IS NOT NULL
    UNION
    SELECT DISTINCT proxy_number AS card_number_proxy, tenant_code
    FROM fis.consumer_account_history
    WHERE delete_nbr = 0 AND proxy_number IS NOT NULL
),
total_ordered AS (
    SELECT COUNT(DISTINCT nmt.card_number_proxy) AS total_cards_ordered
    FROM fis.non_monetary_transaction nmt
    INNER JOIN card_tenant_mapping ctm ON nmt.card_number_proxy = ctm.card_number_proxy
    WHERE nmt.card_creation_date IS NOT NULL
        AND nmt.source_code_desc NOT IN ('Account Access', 'Create a new purse')
),
total_shipped AS (
    SELECT COUNT(DISTINCT wse.proxy_number) AS total_cards_shipped
    FROM ab_corp.workorder_shipping_events wse
    INNER JOIN card_tenant_mapping ctm ON wse.proxy_number = ctm.card_number_proxy
    WHERE wse.ship_date IS NOT NULL
),
total_activated AS (
    SELECT COUNT(DISTINCT nmt.card_number_proxy) AS total_cards_activated
    FROM fis.non_monetary_transaction nmt
    INNER JOIN card_tenant_mapping ctm ON nmt.card_number_proxy = ctm.card_number_proxy
    WHERE nmt.card_status_desc = 'Active' AND nmt."timestamp_" IS NOT NULL
)
SELECT
    (SELECT total_cards_ordered FROM total_ordered) AS total_cards_ordered,
    (SELECT total_cards_shipped FROM total_shipped) AS total_cards_shipped,
    (SELECT total_cards_activated FROM total_activated) AS total_cards_activated`,
  },
  today: {
    name: 'Card Metrics Today',
    description: 'Retrieves today\'s card metrics for all tenants.',
    endpoint: '/reports/card-metrics/today',
    query: `-- Same as report query with date filters for CURRENT_DATE`,
  },
  transactionPatterns: {
    name: 'Card Transaction Patterns',
    description: 'Retrieves card transaction details with tenant information to analyze top decline and accept merchants. Supports filtering by tenant.',
    endpoint: '/reports/card-metrics/transaction-patterns',
    query: `SELECT
    ctd.transaction_id,
    ctd.txn_local_datetime,
    ctd.merchant_name,
    ctd.purse_name,
    ctd.txn_total_dollars,
    ctd.transaction_type,
    ctd.transaction_type_detail,
    ctd.transaction_description,
    ctd.tenant_code,
    t.tenant_name
FROM etl_core.card_transactions_details ctd
LEFT JOIN tenant.tenant t ON ctd.tenant_code = t.tenant_code AND t.delete_nbr = 0`,
  },
  walletTransactionDetails: {
    name: 'Top Declined Products',
    description: 'Aggregates item-level transaction data from daily_item_level_v2 to find the top declined products. Groups by product description, category, benefit type, and merchant, counting declines based on response codes (05, 51) or response descriptions containing "decline".',
    endpoint: '/reports/card-metrics/wallet-transaction-details',
    query: `SELECT
    product_description,
    spend_category,
    category_code,
    benefit_type,
    merchant_name,
    response_code,
    response_description,
    derived_request_code_description,
    COUNT(*) as transaction_count,
    SUM(COALESCE(final_price, item_price, 0)) as total_amount,
    COUNT(CASE WHEN response_description ILIKE '%decline%'
                OR derived_request_code_description ILIKE '%decline%'
                OR response_code IN ('05', '51') THEN 1 END) as decline_count,
    SUM(CASE WHEN response_description ILIKE '%decline%'
              OR derived_request_code_description ILIKE '%decline%'
              OR response_code IN ('05', '51')
         THEN COALESCE(final_price, item_price, 0) ELSE 0 END) as decline_amount,
    COUNT(CASE WHEN NOT (response_description ILIKE '%decline%'
                        OR derived_request_code_description ILIKE '%decline%'
                        OR response_code IN ('05', '51')) THEN 1 END) as accept_count,
    SUM(CASE WHEN NOT (response_description ILIKE '%decline%'
                      OR derived_request_code_description ILIKE '%decline%'
                      OR response_code IN ('05', '51'))
         THEN COALESCE(final_price, item_price, 0) ELSE 0 END) as accept_amount
FROM fis_ebt.daily_item_level_v2
WHERE product_description IS NOT NULL AND product_description != ''
GROUP BY product_description, spend_category, category_code, benefit_type,
         merchant_name, response_code, response_description, derived_request_code_description
HAVING COUNT(CASE WHEN response_description ILIKE '%decline%'
                  OR derived_request_code_description ILIKE '%decline%'
                  OR response_code IN ('05', '51') THEN 1 END) > 0
ORDER BY decline_count DESC
LIMIT 100`,
  },
};

export const CONSUMER_QUERIES = {
  search: {
    name: 'Consumer Search',
    description: 'Searches for consumers by member number, member ID, or consumer code.',
    endpoint: '/reports/consumer/search',
    query: `SELECT *
FROM huser.consumer c
JOIN huser.person p ON p.person_id = c.person_id
WHERE mem_nbr LIKE $1
   OR member_id LIKE $1
   OR consumer_code LIKE $1
LIMIT 10`,
  },
  enrollmentHistory: {
    name: 'Consumer Enrollment History',
    description: 'Retrieves enrollment and disenrollment history for a consumer.',
    endpoint: '/reports/consumer/enrollment-history',
    query: `SELECT *
FROM etl_core.consumers_enrolled_disenrolled
WHERE mem_nbr = $1`,
  },
  loginHistory: {
    name: 'Consumer Login History',
    description: 'Retrieves login history for a consumer.',
    endpoint: '/reports/consumer/login-history',
    query: `SELECT *
FROM etl_core.consumer_app_logins
WHERE consumer_id = $1
ORDER BY login_dt DESC`,
  },
  cardValueLoads: {
    name: 'Card Value Loads',
    description: 'Retrieves card value load transactions for a consumer.',
    endpoint: '/reports/consumer/card-value-loads',
    query: `SELECT
    purse_name,
    total_dollars,
    transaction_description,
    transaction_type_detail
FROM etl_core.card_value_loads
WHERE consumer_code = $1`,
  },
  cardProxy: {
    name: 'Card Proxy',
    description: 'Retrieves card proxy information for a consumer.',
    endpoint: '/reports/consumer/card-proxy',
    query: `SELECT
    card_number_proxy,
    active_card_flag
FROM etl_core.ref_card_proxy
WHERE deleted_consumer_key = false
    AND consumer_code = $1`,
  },
  cohort: {
    name: 'Consumer Cohort',
    description: 'Retrieves cohort assignments for a consumer.',
    endpoint: '/reports/consumer/cohort',
    query: `SELECT *
FROM etl_core.ref_consumer_cohort
WHERE consumer_code = $1`,
  },
  wallet: {
    name: 'Consumer Wallet',
    description: 'Retrieves wallet types for a consumer.',
    endpoint: '/reports/consumer/wallet',
    query: `SELECT DISTINCT wallet_type_label
FROM etl_core.ref_consumer_wallet
WHERE consumer_code = $1`,
  },
  cardTransactions: {
    name: 'Card Transactions',
    description: 'Retrieves card transactions for a consumer.',
    endpoint: '/reports/consumer/card-transactions',
    query: `SELECT
    txn_local_datetime,
    merchant_name,
    purse_name,
    txn_total_dollars,
    transaction_type,
    transaction_type_detail,
    transaction_description
FROM etl_core.card_transactions_details
WHERE card_number_proxy = $1
ORDER BY txn_local_datetime DESC`,
  },
  tasks: {
    name: 'Consumer Tasks',
    description: 'Retrieves tasks assigned to a consumer with reward information.',
    endpoint: '/reports/consumer/tasks',
    query: `SELECT
    t.task_id,
    t.task_name,
    json_extract_path_text(tw.reward, 'rewardType') AS reward_type,
    json_extract_path_text(tw.reward, 'rewardAmount')::DECIMAL(10,2) AS reward_amount,
    tw.task_external_code,
    tw.valid_start_ts,
    ct.task_status,
    ct.task_completed_ts
FROM task.task t
INNER JOIN task.task_reward tw ON tw.task_id = t.task_id
INNER JOIN task.consumer_task ct ON ct.task_id = t.task_id AND tw.tenant_code = ct.tenant_code
WHERE ct.consumer_code = $1
ORDER BY ct.task_completed_ts DESC NULLS LAST, t.task_name`,
  },
};

export const CALL_CENTER_QUERIES = {
  dailyHealthSummary: {
    name: 'Daily Call Center Health Summary',
    description: 'Retrieves daily call center metrics including total interactions, answered/abandoned calls, queue times, handle times, and SLA compliance.',
    endpoint: '/reports/call-center/daily-health-summary',
    query: `SELECT
    call_date,
    COUNT(*) AS total_interactions,
    SUM(CASE WHEN is_abandoned_call = 0 THEN 1 ELSE 0 END) AS answered_calls,
    SUM(is_abandoned_call) AS abandoned_calls,
    ROUND(AVG(queue_time), 2) AS avg_queue_time_sec,
    ROUND(AVG(handle_time), 2) AS avg_handle_time_sec,
    ROUND(
        100.0 * SUM(is_answered_within_30_seconds) / NULLIF(COUNT(*), 0),
        2
    ) AS sla_30s_pct
FROM etl_core.call_center_interactions
GROUP BY call_date
ORDER BY call_date DESC`,
  },
  agentProductivityDaily: {
    name: 'Agent Productivity Daily',
    description: 'Retrieves daily agent productivity metrics including interactions handled, talk time, wrap time, and after-call work time.',
    endpoint: '/reports/call-center/agent-productivity-daily',
    query: `SELECT
    call_date,
    agent_id,
    agent_name,
    COUNT(*) AS interactions_handled,
    ROUND(SUM(talk_time)/60.0, 2) AS talk_minutes,
    ROUND(SUM(wrap_time)/60.0, 2) AS wrap_minutes,
    ROUND(SUM(after_conversation_time_seconds)/60.0, 2) AS acw_minutes
FROM etl_core.call_center_interactions
WHERE is_abandoned_call = 0
GROUP BY call_date, agent_id, agent_name
ORDER BY call_date DESC, agent_name`,
  },
  intentPatterns: {
    name: 'Call Intent Patterns',
    description: 'Retrieves distribution of call intents to identify common reasons for calls.',
    endpoint: '/reports/call-center/intent-patterns',
    query: `SELECT
    COALESCE(primary_intent_a, 'Unknown') AS primary_intent_a,
    COALESCE(primary_intent_b, 'Unknown') AS primary_intent_b,
    COUNT(*) AS call_count,
    ROUND(100.0 * COUNT(*) / SUM(COUNT(*)) OVER (), 2) AS percentage
FROM etl_core.call_center_interactions
WHERE primary_intent_a IS NOT NULL OR primary_intent_b IS NOT NULL
GROUP BY primary_intent_a, primary_intent_b
ORDER BY call_count DESC
LIMIT 20`,
  },
  intentTrends: {
    name: 'Call Intent Trends',
    description: 'Retrieves daily trends of call intents over time.',
    endpoint: '/reports/call-center/intent-trends',
    query: `SELECT
    call_date,
    COALESCE(primary_intent_a, 'Unknown') AS primary_intent_a,
    COUNT(*) AS call_count
FROM etl_core.call_center_interactions
WHERE primary_intent_a IS NOT NULL
GROUP BY call_date, primary_intent_a
ORDER BY call_date DESC, call_count DESC`,
  },
};

export const CASE_QUERIES = {
  statistics: {
    name: 'Case Statistics',
    description: 'Retrieves overall case statistics including counts by status and priority.',
    endpoint: '/reports/call-center/case-statistics',
    query: `SELECT
    COUNT(*) AS total_cases,
    COUNT(CASE WHEN status = 'Closed' THEN 1 END) AS closed_cases,
    COUNT(CASE WHEN status = 'Open' THEN 1 END) AS open_cases,
    COUNT(CASE WHEN priority = 'High' THEN 1 END) AS high_priority_cases,
    COUNT(DISTINCT type) AS unique_types,
    COUNT(DISTINCT reason) AS unique_reasons,
    MIN(created_date) AS earliest_case,
    MAX(created_date) AS latest_case
FROM salesforce.case
WHERE description IS NOT NULL`,
  },
  typeDistribution: {
    name: 'Case Type Distribution',
    description: 'Retrieves distribution of cases by type.',
    endpoint: '/reports/call-center/case-type-distribution',
    query: `SELECT
    COALESCE(type, 'Unknown') AS case_type,
    COUNT(*) AS case_count,
    ROUND(100.0 * COUNT(*) / SUM(COUNT(*)) OVER (), 2) AS percentage
FROM salesforce.case
WHERE description IS NOT NULL
GROUP BY type
ORDER BY case_count DESC
LIMIT 20`,
  },
  reasonDistribution: {
    name: 'Case Reason Distribution',
    description: 'Retrieves distribution of cases by reason.',
    endpoint: '/reports/call-center/case-reason-distribution',
    query: `SELECT
    COALESCE(reason, 'Unknown') AS case_reason,
    COUNT(*) AS case_count,
    ROUND(100.0 * COUNT(*) / SUM(COUNT(*)) OVER (), 2) AS percentage
FROM salesforce.case
WHERE description IS NOT NULL
GROUP BY reason
ORDER BY case_count DESC
LIMIT 20`,
  },
  patternsSql: {
    name: 'Case Patterns (SQL)',
    description: 'Identifies common case patterns using keyword analysis on case descriptions.',
    endpoint: '/reports/call-center/case-patterns-sql',
    query: `WITH word_extraction AS (
    SELECT
        id,
        LOWER(description) AS description_lower,
        type,
        reason,
        priority
    FROM salesforce.case
    WHERE description IS NOT NULL AND LENGTH(description) > 10
),
keyword_counts AS (
    SELECT
        CASE
            WHEN description_lower LIKE '%card%' AND description_lower LIKE '%not working%' THEN 'Card Not Working'
            WHEN description_lower LIKE '%card%' AND description_lower LIKE '%activate%' THEN 'Card Activation Issue'
            WHEN description_lower LIKE '%card%' AND description_lower LIKE '%lost%' THEN 'Lost Card'
            WHEN description_lower LIKE '%card%' AND description_lower LIKE '%stolen%' THEN 'Stolen Card'
            WHEN description_lower LIKE '%card%' AND description_lower LIKE '%replace%' THEN 'Card Replacement'
            WHEN description_lower LIKE '%login%' OR description_lower LIKE '%log in%' THEN 'Login Issues'
            WHEN description_lower LIKE '%password%' OR description_lower LIKE '%reset%' THEN 'Password Reset'
            WHEN description_lower LIKE '%refund%' THEN 'Refund Request'
            WHEN description_lower LIKE '%fraud%' OR description_lower LIKE '%unauthorized%' THEN 'Fraud/Unauthorized Activity'
            ELSE 'Other'
        END AS pattern_category,
        id
    FROM word_extraction
)
SELECT
    pattern_category,
    COUNT(*) AS case_count,
    ROUND(100.0 * COUNT(*) / SUM(COUNT(*)) OVER (), 2) AS percentage
FROM keyword_counts
GROUP BY pattern_category
ORDER BY case_count DESC
LIMIT 20`,
  },
};

export const ENROLLMENT_CHECK_QUERIES = {
  kpCohortAssignments: {
    name: 'KP Cohort Assignments Check',
    description: 'Verifies all consumers in the KP tenant are assigned to cohorts 139 or 140.',
    endpoint: '/reports/enrollment/run-check',
    query: `SELECT
    COALESCE(c.onboarding_state, 'NULL') AS onboarding_state,
    COUNT(DISTINCT c.consumer_code) AS consumer_count
FROM huser.consumer c
WHERE c.tenant_code = 'ten-353ae621abde4e22be409325a1dd0eab'
    AND c.delete_nbr = 0
    AND NOT EXISTS (
        SELECT 1
        FROM cohort.cohort_consumer cc
        WHERE cc.consumer_code = c.consumer_code
            AND cc.cohort_id IN (139, 140)
            AND cc.delete_nbr = 0
    )
GROUP BY COALESCE(c.onboarding_state, 'NULL')
ORDER BY consumer_count DESC`,
  },
};

export const REPORTS_QUERIES = {
  salesSummary: {
    name: 'Sales Summary',
    description: 'Retrieves monthly sales summary including order count, revenue, and average order value.',
    endpoint: '/reports/sales-summary',
    query: `SELECT
    DATE_TRUNC('month', order_date) as month,
    COUNT(*) as total_orders,
    SUM(total_amount) as revenue,
    AVG(total_amount) as avg_order_value
FROM sales
WHERE order_date BETWEEN $1 AND $2
GROUP BY DATE_TRUNC('month', order_date)
ORDER BY month DESC`,
  },
  topProducts: {
    name: 'Top Products',
    description: 'Retrieves top selling products by revenue.',
    endpoint: '/reports/top-products',
    query: `SELECT
    product_name,
    SUM(quantity) as units_sold,
    SUM(total_amount) as revenue
FROM sales
GROUP BY product_name
ORDER BY revenue DESC
LIMIT $1`,
  },
  trendData: {
    name: 'Trend Data',
    description: 'Retrieves trend data for revenue or orders over time.',
    endpoint: '/reports/trends',
    query: `SELECT
    TO_CHAR(order_date, 'YYYY-MM-DD') as period,
    SUM(total_amount) as value
FROM sales
WHERE order_date >= DATEADD(month, -3, CURRENT_DATE)
GROUP BY TO_CHAR(order_date, 'YYYY-MM-DD')
ORDER BY period`,
  },
};
